// /admin/calendario-marketing
// Calendario editorial / plan de comunicación de Limpack.
// Vista lista agrupada por día. Filtros por canal/estado/asignado.
// Pensado mobile-first para que el equipo lo use desde el celular.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'

const CANAL_LABEL = {
  instagram_post:   { icon: '📸', label: 'IG Post',     color: '#dd2a7b' },
  instagram_story:  { icon: '📱', label: 'IG Story',    color: '#bc1888' },
  instagram_reel:   { icon: '🎬', label: 'IG Reel',     color: '#8a3ab9' },
  whatsapp_dm:      { icon: '💬', label: 'WhatsApp DM', color: '#25d366' },
  whatsapp_grupo:   { icon: '👥', label: 'WA Grupo',    color: '#25d366' },
  meta_ads:         { icon: '🎯', label: 'Meta Ads',    color: '#1877f2' },
  volante_fisico:   { icon: '📄', label: 'Volante',     color: '#f59e0b' },
  visita_edificio:  { icon: '🏢', label: 'Visita',      color: '#10b981' },
  prensa:           { icon: '📰', label: 'Prensa',      color: '#6b7280' },
  email:            { icon: '✉️', label: 'Email',       color: '#3b82f6' },
  otro:             { icon: '📌', label: 'Otro',        color: '#94a3b8' },
}

const ESTADO_PILL = {
  pendiente:   { bg: '#fef3c7', color: '#92400e', label: '⏳ Pendiente' },
  en_proceso:  { bg: '#dbeafe', color: '#1e40af', label: '🔄 En proceso' },
  publicado:   { bg: '#d1fae5', color: '#065f46', label: '✅ Publicado' },
  saltado:     { bg: '#f1f5f9', color: '#64748b', label: '⏭️ Saltado' },
  cancelado:   { bg: '#fee2e2', color: '#991b1b', label: '❌ Cancelado' },
}

const fmtDia = (d) => new Date(d + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'short' })
const fmtHora = (h) => h ? h.slice(0, 5) : ''

// Detectar si una fecha es HOY / MAÑANA / PASADA / FUTURA
function diaTipo(fecha) {
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0)
  const f = new Date(fecha + 'T12:00:00'); f.setHours(0, 0, 0, 0)
  const diff = Math.round((f - hoy) / (1000 * 60 * 60 * 24))
  if (diff < 0) return 'pasada'
  if (diff === 0) return 'hoy'
  if (diff === 1) return 'mañana'
  return 'futura'
}

export default function CalendarioMarketing() {
  const { user } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState('activos')   // activos | todos | publicado | pendiente
  const [filtroCanal, setFiltroCanal] = useState('todos')
  const [editando, setEditando] = useState(null)  // null | 'nuevo' | { item }

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    const { data, error } = await supabase
      .from('calendario_marketing')
      .select('*, asignado:profiles!calendario_marketing_asignado_a_fkey(nombre)')
      .order('fecha', { ascending: true })
      .order('hora_sugerida', { ascending: true, nullsFirst: false })
    if (error) console.error(error)
    setItems(data || [])
    setLoading(false)
  }

  // Filtros + agrupación por día
  const filtrados = useMemo(() => {
    let r = items
    if (filtroEstado === 'activos') r = r.filter(i => !['publicado', 'saltado', 'cancelado'].includes(i.estado))
    else if (filtroEstado !== 'todos') r = r.filter(i => i.estado === filtroEstado)
    if (filtroCanal !== 'todos') r = r.filter(i => i.canal === filtroCanal)
    return r
  }, [items, filtroEstado, filtroCanal])

  const agrupados = useMemo(() => {
    const map = new Map()
    filtrados.forEach(i => {
      if (!map.has(i.fecha)) map.set(i.fecha, [])
      map.get(i.fecha).push(i)
    })
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [filtrados])

  const counts = useMemo(() => ({
    pendiente: items.filter(i => i.estado === 'pendiente').length,
    en_proceso: items.filter(i => i.estado === 'en_proceso').length,
    publicado: items.filter(i => i.estado === 'publicado').length,
    hoy: items.filter(i => diaTipo(i.fecha) === 'hoy' && !['publicado', 'saltado', 'cancelado'].includes(i.estado)).length,
  }), [items])

  async function publicarRapido(item) {
    try {
      const url = prompt('URL del post publicado (opcional):', item.publicado_url || '')
      if (url === null) return  // canceló
      const { error } = await supabase.rpc('marcar_pieza_publicada', {
        p_id: item.id,
        p_url: url || null,
      })
      if (error) throw error
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function copiar(texto) {
    await navigator.clipboard.writeText(texto)
    alert('Copiado al portapapeles')
  }

  return (
    <AdminLayout>
      <div className="page">
        <h1>📣 Calendario de Marketing</h1>
        <p className="muted">Plan editorial del equipo. Compartido entre vos, Roberto y quien sume.</p>

        {/* Stats */}
        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Hoy</h3>
            <div className="stat" style={{ color: counts.hoy > 0 ? '#ef4444' : '#10B981' }}>{counts.hoy}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>pendientes</div>
          </div>
          <div className="card">
            <h3>Pendientes total</h3>
            <div className="stat" style={{ color: '#f59e0b' }}>{counts.pendiente}</div>
          </div>
          <div className="card">
            <h3>En proceso</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>{counts.en_proceso}</div>
          </div>
          <div className="card">
            <h3>Publicadas</h3>
            <div className="stat" style={{ color: '#10B981' }}>{counts.publicado}</div>
          </div>
        </div>

        {/* Acciones + filtros */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <button onClick={() => setEditando('nuevo')}
            style={{ background: '#1a3461', color: '#fff', border: 0, padding: '10px 18px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            + Nueva pieza
          </button>

          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
            <option value="activos">📋 Activos (no publicados)</option>
            <option value="todos">👁️ Todos</option>
            <option value="pendiente">⏳ Solo pendientes</option>
            <option value="en_proceso">🔄 Solo en proceso</option>
            <option value="publicado">✅ Solo publicados</option>
          </select>

          <select value={filtroCanal} onChange={e => setFiltroCanal(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
            <option value="todos">Todos los canales</option>
            {Object.entries(CANAL_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v.icon} {v.label}</option>
            ))}
          </select>
        </div>

        {loading && <div style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>Cargando…</div>}

        {!loading && agrupados.length === 0 && (
          <div style={{ padding: 60, textAlign: 'center', background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 12, color: '#6b7280' }}>
            <div style={{ fontSize: 48 }}>📭</div>
            <h3 style={{ color: '#1a3461' }}>Sin piezas en el calendario</h3>
            <p>Hacé click en "+ Nueva pieza" para arrancar.</p>
          </div>
        )}

        {/* Agrupado por día */}
        <div className="cm-list">
          {agrupados.map(([fecha, piezas]) => {
            const tipo = diaTipo(fecha)
            return (
              <div key={fecha} className={`cm-dia cm-dia-${tipo}`}>
                <div className="cm-dia-header">
                  <span className="cm-dia-fecha">{fmtDia(fecha)}</span>
                  {tipo === 'hoy' && <span className="cm-dia-badge cm-badge-hoy">HOY</span>}
                  {tipo === 'mañana' && <span className="cm-dia-badge cm-badge-manana">Mañana</span>}
                  {tipo === 'pasada' && <span className="cm-dia-badge cm-badge-pasada">⚠️ Atrasado</span>}
                  <span className="cm-dia-count">{piezas.length} pieza{piezas.length !== 1 ? 's' : ''}</span>
                </div>

                <div className="cm-piezas">
                  {piezas.map(p => {
                    const canal = CANAL_LABEL[p.canal] || CANAL_LABEL.otro
                    const estado = ESTADO_PILL[p.estado] || ESTADO_PILL.pendiente
                    return (
                      <div key={p.id} className="cm-pieza">
                        <div className="cm-pieza-head">
                          <div className="cm-canal" style={{ background: canal.color + '15', color: canal.color }}>
                            <span style={{ fontSize: 14 }}>{canal.icon}</span>
                            <span>{canal.label}</span>
                          </div>
                          {p.hora_sugerida && <span className="cm-hora">🕐 {fmtHora(p.hora_sugerida)}</span>}
                          <span className="cm-estado" style={{ background: estado.bg, color: estado.color }}>
                            {estado.label}
                          </span>
                        </div>

                        <div className="cm-pieza-titulo">{p.titulo}</div>

                        {p.copy && (
                          <div className="cm-copy">
                            <pre>{p.copy}</pre>
                            <button className="cm-copy-btn" onClick={() => copiar(p.copy)}>📋 Copiar</button>
                          </div>
                        )}

                        {p.descripcion_visual && (
                          <div className="cm-meta-row">🎨 <strong>Visual:</strong> {p.descripcion_visual}</div>
                        )}
                        {p.hashtags && (
                          <div className="cm-meta-row cm-hashtags" onClick={() => copiar(p.hashtags)}>
                            🏷️ {p.hashtags} <span style={{ fontSize: 10, color: '#94a3b8' }}>(click para copiar)</span>
                          </div>
                        )}
                        {p.url_link && (
                          <div className="cm-meta-row">🔗 <a href={p.url_link} target="_blank" rel="noopener">{p.url_link}</a></div>
                        )}
                        {p.asignado?.nombre && (
                          <div className="cm-meta-row">👤 <strong>{p.asignado.nombre}</strong></div>
                        )}
                        {p.notas_internas && (
                          <div className="cm-notas">📝 {p.notas_internas}</div>
                        )}

                        <div className="cm-acciones">
                          {p.estado !== 'publicado' && (
                            <button onClick={() => publicarRapido(p)} className="cm-btn cm-btn-success">
                              ✅ Marcar publicado
                            </button>
                          )}
                          <button onClick={() => setEditando({ item: p })} className="cm-btn cm-btn-ghost">
                            ✏️ Editar
                          </button>
                          {p.publicado_url && (
                            <a href={p.publicado_url} target="_blank" rel="noopener" className="cm-btn cm-btn-ghost">
                              🔗 Ver publicado
                            </a>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {editando && <ModalPieza item={editando === 'nuevo' ? null : editando.item} onClose={() => { setEditando(null); cargar() }} />}

      <Estilos />
    </AdminLayout>
  )
}

function ModalPieza({ item, onClose }) {
  const isNuevo = !item
  const [form, setForm] = useState(item || {
    fecha: new Date().toISOString().slice(0, 10),
    hora_sugerida: '',
    canal: 'instagram_post',
    titulo: '',
    copy: '',
    descripcion_visual: '',
    hashtags: '#LimpackUY #LimpiezaResueltaCadaMes',
    url_link: '',
    notas_internas: '',
    estado: 'pendiente',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function guardar() {
    setLoading(true); setError(null)
    try {
      if (isNuevo) {
        const { error } = await supabase.from('calendario_marketing').insert({
          ...form,
          hora_sugerida: form.hora_sugerida || null,
        })
        if (error) throw error
      } else {
        const { error } = await supabase.from('calendario_marketing').update({
          ...form,
          hora_sugerida: form.hora_sugerida || null,
        }).eq('id', item.id)
        if (error) throw error
      }
      onClose()
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }

  async function eliminar() {
    if (!confirm('¿Eliminar esta pieza? No se puede deshacer.')) return
    setLoading(true)
    try {
      const { error } = await supabase.from('calendario_marketing').delete().eq('id', item.id)
      if (error) throw error
      onClose()
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }

  return (
    <div className="cm-modal-bg" onClick={onClose}>
      <div className="cm-modal" onClick={e => e.stopPropagation()}>
        <h2>{isNuevo ? '+ Nueva pieza' : '✏️ Editar pieza'}</h2>

        <div className="cm-form-row">
          <label>Fecha</label>
          <input type="date" value={form.fecha} onChange={e => set('fecha', e.target.value)} />
        </div>

        <div className="cm-form-row">
          <label>Hora sugerida (opcional)</label>
          <input type="time" value={form.hora_sugerida || ''} onChange={e => set('hora_sugerida', e.target.value)} />
        </div>

        <div className="cm-form-row">
          <label>Canal</label>
          <select value={form.canal} onChange={e => set('canal', e.target.value)}>
            {Object.entries(CANAL_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v.icon} {v.label}</option>
            ))}
          </select>
        </div>

        <div className="cm-form-row">
          <label>Título / Resumen</label>
          <input type="text" value={form.titulo} onChange={e => set('titulo', e.target.value)} placeholder="Ej: Post de presentación de Limpack" />
        </div>

        <div className="cm-form-row">
          <label>Copy del mensaje (lo que se publica)</label>
          <textarea value={form.copy || ''} onChange={e => set('copy', e.target.value)} rows={6} />
        </div>

        <div className="cm-form-row">
          <label>Descripción del visual</label>
          <textarea value={form.descripcion_visual || ''} onChange={e => set('descripcion_visual', e.target.value)} rows={2} placeholder="Ej: Foto del kit con fondo azul navy, isotipo abajo a la derecha" />
        </div>

        <div className="cm-form-row">
          <label>Hashtags</label>
          <input type="text" value={form.hashtags || ''} onChange={e => set('hashtags', e.target.value)} />
        </div>

        <div className="cm-form-row">
          <label>URL link (opcional)</label>
          <input type="url" value={form.url_link || ''} onChange={e => set('url_link', e.target.value)} placeholder="https://…" />
        </div>

        <div className="cm-form-row">
          <label>Notas internas (no se publican)</label>
          <textarea value={form.notas_internas || ''} onChange={e => set('notas_internas', e.target.value)} rows={2} placeholder="Ej: pedir foto a Roberto" />
        </div>

        <div className="cm-form-row">
          <label>Estado</label>
          <select value={form.estado} onChange={e => set('estado', e.target.value)}>
            {Object.entries(ESTADO_PILL).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>

        {error && <div className="cm-error">{error}</div>}

        <div className="cm-modal-acciones">
          <button onClick={guardar} disabled={loading} className="cm-btn cm-btn-primary">
            {loading ? 'Guardando…' : '💾 Guardar'}
          </button>
          <button onClick={onClose} disabled={loading} className="cm-btn cm-btn-ghost">Cancelar</button>
          {!isNuevo && (
            <button onClick={eliminar} disabled={loading} className="cm-btn cm-btn-danger">🗑️ Eliminar</button>
          )}
        </div>
      </div>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      .cm-list { display: flex; flex-direction: column; gap: 18px; }
      .cm-dia { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
      .cm-dia-hoy { border-color: #ef4444; box-shadow: 0 4px 14px rgba(239, 68, 68, 0.12); }
      .cm-dia-mañana { border-color: #f59e0b; }
      .cm-dia-pasada { background: #fef2f2; border-color: #fca5a5; }

      .cm-dia-header {
        background: #f8fafc;
        padding: 12px 16px;
        display: flex; align-items: center; gap: 10px;
        flex-wrap: wrap;
        border-bottom: 1px solid #e5e7eb;
      }
      .cm-dia-fecha { font-weight: 800; color: #1a3461; font-size: 14px; text-transform: capitalize; }
      .cm-dia-badge { padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 800; }
      .cm-badge-hoy { background: #fee2e2; color: #991b1b; }
      .cm-badge-manana { background: #fef3c7; color: #92400e; }
      .cm-badge-pasada { background: #fee2e2; color: #991b1b; }
      .cm-dia-count { margin-left: auto; font-size: 11px; color: #94a3b8; }

      .cm-piezas { padding: 12px; display: flex; flex-direction: column; gap: 12px; }
      .cm-pieza {
        background: #fff;
        border: 1px solid #f1f5f9;
        border-radius: 10px;
        padding: 14px;
      }
      .cm-pieza-head { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 8px; }
      .cm-canal {
        display: inline-flex; align-items: center; gap: 5px;
        padding: 3px 10px; border-radius: 6px;
        font-size: 11px; font-weight: 700;
      }
      .cm-hora { font-size: 11px; color: #64748b; }
      .cm-estado { padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; margin-left: auto; }
      .cm-pieza-titulo { font-weight: 700; color: #1a3461; font-size: 14px; margin-bottom: 8px; }

      .cm-copy { background: #f8fafc; border-radius: 8px; padding: 10px 12px; margin: 8px 0; position: relative; }
      .cm-copy pre { margin: 0; font-family: inherit; white-space: pre-wrap; font-size: 13px; color: #334155; line-height: 1.5; }
      .cm-copy-btn {
        position: absolute; top: 8px; right: 8px;
        background: #1a3461; color: #fff;
        border: 0; border-radius: 6px;
        padding: 4px 10px; font-size: 10px; font-weight: 700;
        cursor: pointer;
      }

      .cm-meta-row { font-size: 12px; color: #475569; margin: 4px 0; }
      .cm-hashtags { background: #eff6ff; padding: 6px 10px; border-radius: 6px; font-family: monospace; cursor: pointer; }
      .cm-notas { font-size: 12px; background: #fef3c7; padding: 8px 12px; border-radius: 6px; color: #78350f; margin-top: 8px; }

      .cm-acciones { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 10px; padding-top: 10px; border-top: 1px dashed #f1f5f9; }
      .cm-btn { padding: 6px 12px; border: 0; border-radius: 6px; font-weight: 700; font-size: 12px; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; }
      .cm-btn-success { background: #10B981; color: #fff; }
      .cm-btn-ghost { background: #fff; color: #475569; border: 1px solid #d1d5db; }
      .cm-btn-primary { background: #1a3461; color: #fff; }
      .cm-btn-danger { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; margin-left: auto; }

      .cm-modal-bg { position: fixed; inset: 0; z-index: 1000; background: rgba(15,31,61,.55); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 16px; }
      .cm-modal { background: #fff; border-radius: 16px; max-width: 580px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 24px; }
      .cm-modal h2 { color: #1a3461; margin: 0 0 16px; font-size: 20px; }
      .cm-form-row { margin-bottom: 12px; }
      .cm-form-row label { display: block; font-size: 11px; font-weight: 700; color: #475569; margin-bottom: 4px; text-transform: uppercase; letter-spacing: .03em; }
      .cm-form-row input, .cm-form-row select, .cm-form-row textarea {
        width: 100%; box-sizing: border-box;
        padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
        font-size: 14px; font-family: inherit;
      }
      .cm-form-row textarea { resize: vertical; }
      .cm-modal-acciones { display: flex; gap: 8px; margin-top: 20px; padding-top: 16px; border-top: 1px solid #e2e8f0; }
      .cm-error { background: #fee2e2; color: #991b1b; padding: 10px; border-radius: 8px; margin: 10px 0; font-size: 13px; }
    `}</style>
  )
}
